import { ErrorOutline, VaccinesOutlined } from '@mui/icons-material';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Divider, Stack, Typography } from '@mui/material';

import { PharmaceuticalCompanyListItem } from 'shared/api';

type Props = {
  open: boolean;
  company: PharmaceuticalCompanyListItem | null;
  onClose: () => void;
};

export const CannotDeleteDialog = ({ open, company, onClose }: Props) => {
  if (!company) return null;

  const human = company.vaccineCountHuman ?? 0;
  const pets = company.vaccineCountPets ?? 0;
  const total = company.vaccineCount;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          borderBottom: '1px solid',
          borderColor: 'warning.light',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ErrorOutline />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
              Unable to delete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="strong" sx={{ color: 'text.primary' }}>
                {company.abbreviatedName}
              </Box>{' '}
              is referenced by existing vaccines.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Linked vaccines
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<VaccinesOutlined />}
            label={`${total} total`}
            color="warning"
            variant="filled"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {human > 0 && <Chip label={`${human} human`} size="small" variant="outlined" />}
          {pets > 0 && <Chip label={`${pets} pets`} size="small" variant="outlined" />}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          How to delete this company
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            Open each linked vaccine.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            Change its <strong>manufacturer</strong> to another company.
          </Typography>
          <Typography component="li" variant="body2">
            Come back and delete {company.abbreviatedName}.
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Alternatively, rename the company via <strong>Edit</strong> — all linked vaccines will update automatically.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button onClick={onClose} variant="contained" disableElevation fullWidth>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};
